import { z } from 'zod';
import { ASSET_KINDS, INPUT_ROLES } from './models';
import { CurrencySchema, MoneyStringSchema } from './money';
import { MAX_FALLBACK_MODELS } from './routing';

export const TASK_STATUSES = [
  'queued',
  'preparing',
  'running',
  'recovering',
  'reconciling',
  'finalizing',
  'succeeded',
  'failed',
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const TERMINAL_TASK_STATUSES: readonly TaskStatus[] = ['succeeded', 'failed'];

export const TaskInputSchema = z.object({
  type: z.enum(ASSET_KINDS),
  asset_id: z.string().min(1),
  role: z.enum(INPUT_ROLES),
});
export type TaskInput = z.infer<typeof TaskInputSchema>;

export const CreateVideoTaskSchema = z.object({
  model: z.string().min(1),
  prompt: z.string().max(4000).optional(),
  inputs: z.array(TaskInputSchema).max(20).default([]),
  duration: z.number().int(),
  resolution: z.string(),
  ratio: z.string(),
  max_budget: MoneyStringSchema.optional(),
  callback_url: z.url({ protocol: /^https?$/ }).max(2048).optional(),
  metadata: z
    .record(z.string().max(64), z.string().max(512))
    .refine((m) => Object.keys(m).length <= 32, 'metadata 条目过多（至多 32 条）')
    .refine((m) => new TextEncoder().encode(JSON.stringify(m)).length <= 8192, 'metadata 过大（至多 8 KiB）')
    .optional(),
  provider_options: z.record(z.string(), z.union([z.boolean(), z.number().int(), z.string().max(32)])).optional(),
  /**
   * 跨模型兜底（docs/12 §2）：主模型所有渠道都不可用时，按顺序改用这些模型（最多 3 个），按实际模型计费。
   * 不传则不兜底；与 allow_fallback 同时给出时以本字段为准。
   */
  fallback_models: z.array(z.string().min(1).max(64)).max(MAX_FALLBACK_MODELS).optional(),
  /** 同意使用平台为该模型配置的默认兜底链 */
  allow_fallback: z.boolean().optional(),
});
export type CreateVideoTask = z.infer<typeof CreateVideoTaskSchema>;

export const BillingStatusSchema = z.enum(['reserved', 'pending_settlement', 'settled', 'released', 'waived']);

export const TaskBillingSchema = z.object({
  currency: CurrencySchema,
  status: BillingStatusSchema,
  reserved_amount: z.string(),
  charged_amount: z.string().nullable(),
  released_amount: z.string().nullable(),
});

export const UsageItemSchema = z.object({ type: z.string(), value: z.number(), unit: z.string() });

/**
 * 视频地址给法（后台全局默认 + 按用户覆盖）：platform 只给平台链接（默认）；upstream 只给上游原链接
 * （video_url 即原链接，上游一般 24 小时失效）；both 两个都给（video_url 为平台链接，另附 upstream_video_url）。
 */
export const RESULT_URL_MODES = ['platform', 'upstream', 'both'] as const;
export type ResultUrlMode = (typeof RESULT_URL_MODES)[number];

export const TaskResultSchema = z.object({
  video_url: z.string(),
  url_expires_at: z.string(),
  retained_until: z.string(),
  last_frame_url: z.string().nullable(),
  /** 上游原链接（仅 both 模式返回；上游一般 24 小时失效） */
  upstream_video_url: z.string().nullable().optional(),
  /** 上游原链接的大致失效时间（上游完成时间 + 24 小时） */
  upstream_url_expires_at: z.string().nullable().optional(),
});

export const TaskObjectSchema = z.object({
  id: z.string(),
  object: z.literal('video.generation'),
  status: z.enum(TASK_STATUSES),
  status_detail: z.string().nullable(),
  /** 请求的模型 */
  model: z.string(),
  /** 实际使用的模型：发生跨模型兜底时与 model 不同，计费按此模型 */
  model_used: z.string(),
  model_version: z.string(),
  request_id: z.string(),
  created_at: z.string(),
  finished_at: z.string().nullable(),
  deadline_at: z.string(),
  params: z.object({
    prompt: z.string().nullable(),
    duration: z.number(),
    resolution: z.string(),
    ratio: z.string(),
    inputs: z.array(TaskInputSchema),
  }),
  result: TaskResultSchema.nullable(),
  result_expired: z.boolean(),
  usage: z.array(UsageItemSchema),
  billing: TaskBillingSchema,
  error: z.object({ code: z.string(), message: z.string() }).nullable(),
  metadata: z.record(z.string(), z.string()),
});
export type TaskObject = z.infer<typeof TaskObjectSchema>;

/** 对客时间线（隐藏渠道信息） */
export const CustomerTimelineStepSchema = z.object({
  status: z.enum(TASK_STATUSES),
  at: z.string(),
  label: z.string(),
});
export type CustomerTimelineStep = z.infer<typeof CustomerTimelineStepSchema>;

export const WebhookPayloadSchema = z.object({
  id: z.string(),
  type: z.enum(['task.succeeded', 'task.failed']),
  created_at: z.string(),
  data: TaskObjectSchema,
});
export type WebhookPayload = z.infer<typeof WebhookPayloadSchema>;

export { MoneyStringSchema };
